import { useMemo, Suspense } from 'react'
import { Link } from 'react-router-dom'

import { paths } from 'src/config/paths'
import { useIsCurrentUser } from 'src/shared/hooks/use-is-current-user'

import { useUserReviews } from '../../../api/marketplace/use-user-reviews'
import { ReviewStar } from '../../../assets/review-star'
import { Zillow } from '../../../assets/zillow'
import ReviewCard from '../reviews/review-card'

import ReviewsSectionSkeleton from './reviews-skeleton'

const PublicProfileReviewsSectionContent = ({ userId }: { userId: number }) => {
  const { data } = useUserReviews(userId)
  const isCurrentUser = useIsCurrentUser(userId)
  const reviewsCount = data?.totalReviews ?? 0

  const averageRating = data?.averageRating ?? 0

  const reviews = useMemo(() => {
    if (!data) return []
    return data.reviews
  }, [data])

  const getStarPattern = (position: number) => {
    const rating = averageRating
    const starValue = position + 1
    const difference = rating - starValue

    if (difference >= 0) {
      return 'gold' // Full star
    } else if (difference >= -0.5) {
      return 'half_gold' // Half star
    } else {
      return 'silver' // Empty star
    }
  }

  return (
    <div className="flex flex-col gap-4">
      {isCurrentUser && reviews.length === 0 && (
        <div className="flex w-full flex-col items-center gap-4 rounded-lg border-2 border-dashed bg-accent p-4">
          <p className="text-xl font-bold">No reviews yet</p>
          <p className="text-center text-sm text-muted">
            We pull your client reviews directly from Zillow using your email address. Please ensure your email is
            correct and matches your Zillow account.
          </p>
          <Link
            to={paths.settings.personalInfo.getHref()}
            className="max-w-[250px] rounded-md bg-primary px-4 py-2 font-semibold text-white hover:bg-primary/90"
          >
            Update your email settings
          </Link>
        </div>
      )}
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <Zillow size={85} />
          <h3 className="text-[20px] font-bold">Reviews</h3>
          <p className="text-sm font-semibold text-muted">({reviewsCount} reviews)</p>
        </div>
        <div className="mb-4 flex items-center gap-2">
          <h1 className="text-4xl font-bold">{averageRating.toFixed(1)}</h1>
          <div className="flex items-center gap-2">
            {[...Array(5)].map((_, index) => (
              <ReviewStar key={index} size={25} pattern={getStarPattern(index)} />
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-2">
          {reviews.map((review, index) => (
            <ReviewCard key={review.date || '' + review.rating || '' + index} review={review} />
          ))}
        </div>
      </div>
    </div>
  )
}

const PublicProfileReviewsSection = ({ userId }: { userId: number }) => {
  return (
    <Suspense fallback={<ReviewsSectionSkeleton />}>
      <PublicProfileReviewsSectionContent userId={userId} />
    </Suspense>
  )
}

export default PublicProfileReviewsSection
